import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, 
  FloppyDisk, 
  LockSimple,
  Warning,
  Stethoscope,
  ArrowsLeftRight,
  XCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { VitalSignsSection } from '../components/form/VitalSignsSection';
import { SignatureSection } from '../components/form/SignatureSection';
import { PatientInfoSection } from '../components/form/PatientInfoSection';
import { EventAssessmentSection } from '../components/form/EventAssessmentSection';
import { MedicalHistorySection } from '../components/form/MedicalHistorySection';
import { TransportSection } from '../components/form/TransportSection';
import { TransferCareDialog } from '../components/form/TransferCareDialog';
import { FormProgressBar } from '../components/form/FormProgressBar';
import { MedicationForm } from '../components/form/MedicationForm';
import { computeFormValidation, computeOtcValidation } from '../components/form/formValidation';
import { useAutoSave, loadDraft } from '../components/form/useAutoSave';
import { LOGO_1, LOGO_2, HIPAA_TEXT, RMA_TEXT, INITIAL_FORM_DATA } from '../components/form/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientFormPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const hipaaSignatureRef = useRef(null);
  const rmaSignatureRef = useRef(null);
  const hipaaGuardianSignatureRef = useRef(null);
  const rmaGuardianSignatureRef = useRef(null);
  const providerSignatureRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [eventNames, setEventNames] = useState([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [providers, setProviders] = useState([]);
  const [savedPatientId, setSavedPatientId] = useState(null);
  const [formType, setFormType] = useState('acr');

  const [formData, setFormData] = useState({
    ...INITIAL_FORM_DATA,
    provider_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    provider_certification: user?.certification || '',
  });

  const validation = useMemo(() => 
    formType === 'medication' ? computeOtcValidation(formData) : computeFormValidation(formData), 
    [formData, formType]
  );
  const { lastSaved, isSaving, clearDraft } = useAutoSave(formData, token);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  const fetchEventNames = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/events/active`);
      setEventNames(data);
    } catch (err) {
      console.warn('Event fetch failed (non-critical):', err.message);
    }
  }, []);

  useEffect(() => {
    // Use Eastern Time for form defaults
    const now = new Date();
    const etOptions = { timeZone: 'America/New_York' };
    const etDate = now.toLocaleDateString('en-CA', etOptions); // YYYY-MM-DD format
    const etTime = now.toLocaleTimeString('en-GB', { ...etOptions, hour: '2-digit', minute: '2-digit', hour12: false }); // HH:MM
    setFormData(prev => ({
      ...prev,
      form_date: etDate,
      patient_contact_time: etTime,
      hipaa_date: etDate,
      rma_date: etDate
    }));
    fetchEventNames();

    // Check for saved draft
    loadDraft(token).then(draft => {
      if (draft && draft.first_name) {
        setPendingDraft(draft);
        setShowDraftDialog(true);
      }
    });
  }, [fetchEventNames, token]);

  const fetchProviders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/providers-list`, {});
      setProviders(data.filter(p => p.id !== user?.id));
    } catch (err) {
      console.warn('Provider list fetch failed:', err.message);
    }
  }, [token, user?.id]);

  const saveBeforeTransfer = async () => {
    if (!formData.first_name || !formData.last_name) return null;
    try {
      const hipaaSignature = (hipaaSignatureRef.current && !hipaaSignatureRef.current.isEmpty())
        ? hipaaSignatureRef.current.toDataURL() : formData.hipaa_signature || '';
      const rmaSignature = (rmaSignatureRef.current && !rmaSignatureRef.current.isEmpty())
        ? rmaSignatureRef.current.toDataURL() : formData.rma_signature || '';
      const hipaaGuardianSignature = (hipaaGuardianSignatureRef.current && !hipaaGuardianSignatureRef.current.isEmpty())
        ? hipaaGuardianSignatureRef.current.toDataURL() : formData.hipaa_guardian_signature || '';
      const rmaGuardianSignature = (rmaGuardianSignatureRef.current && !rmaGuardianSignatureRef.current.isEmpty())
        ? rmaGuardianSignatureRef.current.toDataURL() : formData.rma_guardian_signature || '';
      const providerSig = (providerSignatureRef.current && !providerSignatureRef.current.isEmpty())
        ? providerSignatureRef.current.toDataURL() : formData.provider_signature || '';

      const submitData = {
        ...formData,
        form_type: formType,
        hipaa_signature: hipaaSignature,
        rma_signature: rmaSignature,
        hipaa_guardian_signature: hipaaGuardianSignature,
        rma_guardian_signature: rmaGuardianSignature,
        provider_signature: providerSig,
      };
      delete submitData._scanned_id_images;

      const { data } = await axios.post(`${API_URL}/api/patients`, submitData);

      // Attach scanned ID images
      if (formData._scanned_id_images?.length > 0 && data.id) {
        for (const img of formData._scanned_id_images) {
          try {
            await axios.post(`${API_URL}/api/patients/${data.id}/id-images`, {
              patient_id: data.id, image_base64: img.base64, side: img.side, doc_type: 'scanned_id',
            });
          } catch { /* non-critical */ }
        }
      }

      setSavedPatientId(data.id);
      return data.id;
    } catch {
      toast.error('Failed to save patient before transfer');
      return null;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleSubmitClick = () => {
    if (validation.missing.length > 0) {
      setShowSubmitDialog(true);
      return;
    }
    setShowSubmitDialog(true);
  };

  const handleConfirmSubmit = async () => {
    if (validation.missing.length > 0) {
      // Dialog is showing missing fields — don't submit
      return;
    }
    await handleSaveOrSubmit(true);
  };

  const handleSaveOrSubmit = async (autoClose = false) => {
    if (!formData.first_name || !formData.last_name) {
      toast.error('Please enter patient first and last name');
      return;
    }

    setLoading(true);

    let submitData = { ...formData, form_type: formType };

    if (formType === 'acr') {
      // ACR: capture signature canvases
      const hipaaSignature = (hipaaSignatureRef.current && !hipaaSignatureRef.current.isEmpty())
        ? hipaaSignatureRef.current.toDataURL() : formData.hipaa_signature || '';
      const rmaSignature = (rmaSignatureRef.current && !rmaSignatureRef.current.isEmpty())
        ? rmaSignatureRef.current.toDataURL() : formData.rma_signature || '';
      const hipaaGuardianSignature = (hipaaGuardianSignatureRef.current && !hipaaGuardianSignatureRef.current.isEmpty())
        ? hipaaGuardianSignatureRef.current.toDataURL() : formData.hipaa_guardian_signature || '';
      const rmaGuardianSignature = (rmaGuardianSignatureRef.current && !rmaGuardianSignatureRef.current.isEmpty())
        ? rmaGuardianSignatureRef.current.toDataURL() : formData.rma_guardian_signature || '';
      const providerSignature = (providerSignatureRef.current && !providerSignatureRef.current.isEmpty())
        ? providerSignatureRef.current.toDataURL() : formData.provider_signature || '';

      submitData = {
        ...submitData,
        hipaa_signature: hipaaSignature,
        rma_signature: rmaSignature,
        hipaa_guardian_signature: hipaaGuardianSignature,
        rma_guardian_signature: rmaGuardianSignature,
        provider_signature: providerSignature,
      };
    }
    // OTC: otc_signature is already in formData via handleInputChange

    // Remove internal fields not for the API
    delete submitData._scanned_id_images;

    try {
      const url = autoClose
        ? `${API_URL}/api/patients?auto_close=true`
        : `${API_URL}/api/patients`;
      const { data: savedPatient } = await axios.post(url, submitData);

      // Attach scanned ID images if any
      if (formData._scanned_id_images?.length > 0 && savedPatient.id) {
        for (const img of formData._scanned_id_images) {
          try {
            await axios.post(`${API_URL}/api/patients/${savedPatient.id}/id-images`, {
              patient_id: savedPatient.id,
              image_base64: img.base64,
              side: img.side,
              doc_type: 'scanned_id',
            });
          } catch {
            console.warn('Failed to attach ID image');
          }
        }
      }

      toast.success(autoClose ? 'Patient record submitted and closed' : 'Patient record saved (Open)');
      await clearDraft();
      navigate('/patients');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create patient record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="patient-form-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="back-to-dashboard">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft size={18} className="mr-2" />
                  Dashboard
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <img src={LOGO_1} alt="Logo 1" className="h-8 object-contain" />
                <img src={LOGO_2} alt="Logo 2" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Auto-save status */}
              {isSaving && (
                <span className="text-xs text-blue-500 animate-pulse" data-testid="autosave-saving">Saving draft...</span>
              )}
              {!isSaving && lastSaved && (
                <span className="text-xs text-green-600" data-testid="autosave-saved">
                  Draft saved {lastSaved.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
              <span className="text-sm text-slate-500">
                {user?.first_name} {user?.last_name}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">

          {/* Form Type Selector */}
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Form Type</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formType === 'acr' ? 'default' : 'outline'}
                    className={`rounded-sm ${formType === 'acr' ? 'bg-slate-900 hover:bg-slate-800' : ''}`}
                    onClick={() => setFormType('acr')}
                    data-testid="form-type-acr"
                  >
                    ACR (Patient Care Report)
                  </Button>
                  <Button
                    type="button"
                    variant={formType === 'medication' ? 'default' : 'outline'}
                    className={`rounded-sm ${formType === 'medication' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    onClick={() => setFormType('medication')}
                    data-testid="form-type-medication"
                  >
                    OTC Medication
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {formType === 'acr' ? (
            <>
          {/* Progress Bar */}
          <FormProgressBar formData={formData} />

          {/* Transfer Care Button */}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-purple-300 text-purple-700 hover:bg-purple-50"
              disabled={!formData.first_name || !formData.last_name}
              onClick={() => { fetchProviders(); setShowTransferDialog(true); }}
              data-testid="transfer-care-button"
            >
              <ArrowsLeftRight size={18} className="mr-2" />
              Transfer Care
            </Button>
          </div>

          <PatientInfoSection formData={formData} handleInputChange={handleInputChange} />
          <EventAssessmentSection formData={formData} handleInputChange={handleInputChange} eventNames={eventNames} />
          <MedicalHistorySection formData={formData} handleInputChange={handleInputChange} handleNestedChange={handleNestedChange} />

          <VitalSignsSection
            formData={formData}
            handleNestedChange={handleNestedChange}
            handleInputChange={handleInputChange}
          />

          {/* Physical Assessment */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Physical Assessment & Findings *</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                value={formData.physical_assessment}
                onChange={(e) => handleInputChange('physical_assessment', e.target.value)}
                placeholder="Enter detailed physical assessment and findings (minimum 40 characters)..."
                className="rounded-sm border-slate-300 min-h-[150px]"
                data-testid="physical-assessment-input"
              />
              <p className={`text-xs ${(formData.physical_assessment || '').length >= 40 ? 'text-green-600' : 'text-slate-400'}`}>
                {(formData.physical_assessment || '').length}/40 characters minimum
              </p>
            </CardContent>
          </Card>

          <TransportSection formData={formData} handleInputChange={handleInputChange} />

          {/* HIPAA Signature */}
          <SignatureSection
            type="hipaa"
            title="HIPAA Privacy Authorization & Disclaimer *"
            iconColor="text-green-600"
            legalText={HIPAA_TEXT}
            minorDisclaimer="I certify that I am the parent, legal guardian, or authorized representative of the minor listed below. I give permission for medical evaluation and treatment as deemed necessary by medical personnel."
            formData={formData}
            handleInputChange={handleInputChange}
            signatureRef={hipaaSignatureRef}
            guardianSignatureRef={hipaaGuardianSignatureRef}
            clearSignature={() => hipaaSignatureRef.current?.clear()}
            clearGuardianSignature={() => hipaaGuardianSignatureRef.current?.clear()}
          />

          {/* RMA Signature */}
          <SignatureSection
            type="rma"
            title="Refusal of Medical Assistance (RMA) / Transport Disclaimer"
            iconColor="text-orange-600"
            legalText={RMA_TEXT}
            minorDisclaimer="I certify that I am the parent, legal guardian, or authorized representative of the minor listed below. I am signing refusing treatment and/or transport on behalf of the child and assume all responsibility hereafter."
            formData={formData}
            handleInputChange={handleInputChange}
            signatureRef={rmaSignatureRef}
            guardianSignatureRef={rmaGuardianSignatureRef}
            clearSignature={() => rmaSignatureRef.current?.clear()}
            clearGuardianSignature={() => rmaGuardianSignatureRef.current?.clear()}
          />

          {/* Provider Signature */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <Stethoscope size={24} className="text-teal-600" />
                Provider Signature
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Provider Name</Label>
                  <Input
                    value={formData.provider_name}
                    disabled
                    className="rounded-sm border-slate-300 bg-slate-50"
                    data-testid="provider-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Level of Certification</Label>
                  <Input
                    value={formData.provider_certification}
                    disabled
                    className="rounded-sm border-slate-300 bg-slate-50"
                    data-testid="provider-certification-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Signature</Label>
                <div className="border border-slate-300 rounded-sm bg-white">
                  <SignatureCanvas
                    ref={providerSignatureRef}
                    canvasProps={{ className: 'w-full h-32', 'data-testid': 'provider-signature-canvas' }}
                    penColor="black"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => providerSignatureRef.current?.clear()}
                  className="text-xs text-slate-500"
                  data-testid="clear-provider-signature"
                >
                  Clear Signature
                </Button>
              </div>
            </CardContent>
          </Card>

          </>) : (
            /* Medication Form */
            <MedicationForm formData={formData} handleInputChange={handleInputChange} />
          )}

          {/* Event Selection (shown for both form types) */}
          {formType === 'medication' && eventNames.length > 0 && (
            <Card className="border-slate-200">
              <CardContent className="py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Event</Label>
                    <select
                      value={formData.event_name || ''}
                      onChange={(e) => handleInputChange('event_name', e.target.value)}
                      className="flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                      data-testid="med-event-select"
                    >
                      <option value="">Select event</option>
                      {eventNames.map(ev => <option key={ev.id} value={ev.name}>{ev.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Form Date</Label>
                    <Input type="date" value={formData.form_date} disabled className="rounded-sm border-slate-300 bg-slate-100 cursor-not-allowed" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Save & Submit Buttons */}
          <div className="flex justify-end gap-4 pb-8">
            <Link to="/dashboard">
              <Button type="button" variant="outline" className="rounded-sm">
                Cancel
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-green-300 text-green-700 hover:bg-green-50"
              disabled={loading}
              onClick={() => handleSaveOrSubmit(false)}
              data-testid="save-patient-form-button"
            >
              <FloppyDisk size={18} className="mr-2" />
              {loading ? 'Saving...' : 'Save (Keep Open)'}
            </Button>
            <Button
              type="button"
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              disabled={loading}
              onClick={handleSubmitClick}
              data-testid="submit-patient-form-button"
            >
              <LockSimple size={18} className="mr-2" />
              {loading ? 'Submitting...' : 'Submit & Close'}
            </Button>
          </div>
        </form>

        {/* Submit Confirmation Dialog — shows missing fields or confirmation */}
        <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              {validation.missing.length > 0 ? (
                <>
                  <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                    <XCircle size={24} weight="fill" />
                    Cannot Submit — Missing Required Fields
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="text-sm text-slate-600 leading-relaxed">
                      <p className="mb-3">Please complete the following fields before submitting:</p>
                      <ul className="space-y-1.5" data-testid="missing-fields-list">
                        {validation.missing.map((field) => (
                          <li key={field} className="flex items-start gap-2 text-red-700">
                            <XCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-red-400" />
                            {field}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </AlertDialogDescription>
                </>
              ) : (
                <>
                  <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                    <Warning size={24} weight="fill" />
                    Notice Before Submission
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-sm text-slate-600 leading-relaxed">
                    Please review your information carefully before submitting this form. Once submitted, your responses are final and cannot be modified, edited, or undone.
                  </AlertDialogDescription>
                </>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-submit-dialog">
                {validation.missing.length > 0 ? 'Go Back & Complete' : 'Cancel'}
              </AlertDialogCancel>
              {validation.missing.length === 0 && (
                <AlertDialogAction
                  onClick={handleConfirmSubmit}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="confirm-submit-button"
                >
                  Submit & Close
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Transfer Care Dialog */}
        <TransferCareDialog
          open={showTransferDialog}
          onOpenChange={setShowTransferDialog}
          patientId={savedPatientId}
          providers={providers}
          token={token}
          onTransferComplete={() => navigate('/patients')}
          saveBeforeTransfer={saveBeforeTransfer}
        />

        {/* Draft Restore Dialog */}
        <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-blue-600">
                <FloppyDisk size={24} />
                Unsaved Draft Found
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-slate-600 leading-relaxed">
                You have an auto-saved draft for patient <strong>{pendingDraft?.first_name} {pendingDraft?.last_name}</strong>. Would you like to restore it?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => { setShowDraftDialog(false); clearDraft(); setPendingDraft(null); }}
                data-testid="discard-draft-button"
              >
                Discard Draft
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (pendingDraft) {
                    setFormData(prev => ({
                      ...prev,
                      ...pendingDraft,
                      provider_name: prev.provider_name,
                      provider_certification: prev.provider_certification,
                    }));
                    toast.success('Draft restored successfully');
                  }
                  setShowDraftDialog(false);
                  setPendingDraft(null);
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="restore-draft-button"
              >
                Restore Draft
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
